/**
 * Presence tiles — the "Where you are today" tri-state in the profile
 * dropdown (§10, screen `41-profile-panel`).
 *
 * Framework-free on purpose: the mapping between the server's status
 * vocabulary and the three tiles the viewer actually sees is the only part of
 * this surface with a wrong answer, so it is testable without a TestBed.
 *
 * THE LOAD-BEARING RULE (§10): "presence and the desk are one decision".
 * There is no server status called "In office" — the platform's
 * `availableStatuses` are the three AWAY-ish ones below. Being in the office
 * IS having a desk booked for today, which is why the office tile is derived
 * from the booking rather than from a status, and why picking it has to lead
 * to a desk rather than writing a status nobody stores.
 *
 * The precedence deliberately mirrors `ProfileDataService.derivedStatus`
 * (booking first, then manual status). If it did not, the avatar dot in the
 * shell bar and the tile directly under it could disagree about where the
 * same person is, which is worse than either answer being wrong.
 */

/** The three tiles, in the order `41-profile-panel` renders them. */
export type PresenceTile = 'office' | 'home' | 'away';

/** Canonical (English) status name the platform uses for working from home. */
export const HOME_STATUS_NAME = 'Working From Home';

/**
 * Canonical status names that read as "not at work today" on the Away tile.
 * Both are real `availableStatuses` values; "Out of Office" is preferred when
 * the viewer picks Away because it is the stronger, more common statement.
 */
export const AWAY_STATUS_NAMES: readonly string[] = [
  'Out of Office',
  'Working Elsewhere',
];

export interface PresenceInput {
  /**
   * Does the viewer hold a desk today, AS `derivedStatus` COUNTS IT — live
   * now, or still to come?
   *
   * Not "any desk booking dated today". The caller reads
   * `derivedStatus.isFromBooking` rather than
   * `ProfileDataService.deskBooking`, whose `find(b => b.bookingType ===
   * 'desk')` has no time filter: a desk that ENDED this morning satisfies it
   * and not `derivedStatus`, which is how the tile and the avatar dot above
   * it used to end up asserting opposite things about the same day.
   */
  readonly hasDeskToday: boolean;
  /** Today holds the in-office status (a `location` booking, set from Office). */
  readonly inOfficeToday?: boolean;
  /** Canonical English name of the manually-set status, if any. */
  readonly statusName: string | null | undefined;
}

/**
 * Which tile is selected. `null` means none of them are — the viewer has
 * neither booked a desk nor said where they are, and the panel must not
 * invent an answer by lighting one up.
 */
export function selectedPresenceTile(input: PresenceInput): PresenceTile | null {
  if (input.hasDeskToday || input.inOfficeToday) return 'office';
  const name = input.statusName ?? null;
  if (!name) return null;
  if (name === HOME_STATUS_NAME) return 'home';
  if (AWAY_STATUS_NAMES.includes(name)) return 'away';
  // An unrecognised status is a real statement about the viewer, just not one
  // of these three. Lighting up "Away" because it is the nearest tile would
  // put words in their mouth.
  return null;
}

/**
 * The status to write when a tile is picked, chosen from what the server
 * actually offers this customer rather than from a hardcoded id.
 *
 * Returns `null` for the office tile (Office is not one of these status
 * names: it is set through `ProfileDataService.setInOffice`) and for a tile whose status this
 * customer has not enabled, which the caller must treat as "cannot set"
 * rather than silently doing nothing visible.
 */
export function statusNameForTile(
  tile: PresenceTile,
  available: readonly string[],
): string | null {
  if (tile === 'office') return null;
  if (tile === 'home') {
    return available.includes(HOME_STATUS_NAME) ? HOME_STATUS_NAME : null;
  }
  return AWAY_STATUS_NAMES.find(name => available.includes(name)) ?? null;
}
